import { Controller } from "@hotwired/stimulus"

// Form validation controller for real-time field validation
export default class extends Controller {
  static targets = ["field", "form", "submitButton", "errorContainer"]
  static values = {
    formTemplateId: Number,
    validateUrl: String,
    validateFormUrl: String
  }

  connect() {
    // Initialize validation state
    this.validationState = {}
    
    // Add event listeners to all field targets
    this.fieldTargets.forEach(field => {
      field.addEventListener('blur', this.validateField.bind(this))
      field.addEventListener('change', this.validateField.bind(this))
    })
    
    // Add form submission validation
    if (this.hasFormTarget) {
      this.formTarget.addEventListener('submit', this.validateForm.bind(this))
    }
  }
  
  // Validate a single field
  async validateField(event) {
    const field = event.target
    const fieldId = field.dataset.fieldId
    const value = field.value
    const required = field.dataset.required === 'true'
    
    // Skip validation for empty optional fields
    if (!value && !required) {
      this.clearFieldError(fieldId)
      this.validationState[fieldId] = true
      this.updateSubmitButton()
      return
    }
    
    try {
      const response = await fetch(this.validateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          field_id: fieldId,
          value: value
        })
      })
      
      const result = await response.json()
      
      if (result.valid) {
        this.clearFieldError(fieldId)
        this.validationState[fieldId] = true
      } else {
        this.showFieldError(fieldId, result.message)
        this.validationState[fieldId] = false
      }
      
      this.updateSubmitButton()
    } catch (error) {
      console.error('Validation error:', error)
    }
  }
  
  // Validate the entire form before submission
  async validateForm(event) {
    if (this.hasSubmitButtonTarget && this.submitButtonTarget.disabled) {
      event.preventDefault()
      return
    }
    
    // Collect all form responses
    const responses = {}
    this.fieldTargets.forEach(field => {
      responses[field.dataset.fieldId] = field.value
    })
    
    try {
      const response = await fetch(this.validateFormUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          form_template_id: this.formTemplateIdValue,
          responses: responses
        })
      })
      
      const result = await response.json()
      
      if (!result.valid) {
        event.preventDefault()
        
        // Show errors for each invalid field
        Object.entries(result.field_errors).forEach(([fieldId, message]) => {
          this.showFieldError(fieldId, message)
          this.validationState[fieldId] = false
        })
        
        // Show general error message
        if (this.hasErrorContainerTarget) {
          this.errorContainerTarget.textContent = 'Please fix the errors before submitting.'
          this.errorContainerTarget.classList.remove('hidden')
        }
        
        this.updateSubmitButton()
      }
    } catch (error) {
      console.error('Form validation error:', error)
      event.preventDefault()
    }
  }
  
  // Show error message for a field
  showFieldError(fieldId, message) {
    const field = this.fieldTargets.find(f => f.dataset.fieldId === fieldId)
    if (!field) return
    
    // Add error class to the field
    field.classList.add('is-invalid')
    
    // Find or create error message container
    let errorElement = document.getElementById(`error-${fieldId}`)
    if (!errorElement) {
      errorElement = document.createElement('div')
      errorElement.id = `error-${fieldId}`
      errorElement.classList.add('invalid-feedback')
      field.parentNode.appendChild(errorElement)
    }
    
    errorElement.textContent = message
    errorElement.classList.remove('hidden')
  }
  
  // Clear error message for a field
  clearFieldError(fieldId) {
    const field = this.fieldTargets.find(f => f.dataset.fieldId === fieldId)
    if (!field) return
    
    // Remove error class from the field
    field.classList.remove('is-invalid')
    
    // Hide error message
    const errorElement = document.getElementById(`error-${fieldId}`)
    if (errorElement) {
      errorElement.classList.add('hidden')
    }
  }
  
  // Update submit button state based on validation
  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return
    
    const allValid = Object.values(this.validationState).every(valid => valid)
    this.submitButtonTarget.disabled = !allValid
  }
}